import React from 'react';
import styled from 'styled-components';
import { UploadCloud } from 'react-feather';
import { uiTokens } from './tokens';

/**
 * Full-viewport drop placeholder, driven by `isDragging` from `useFileDrop`.
 *
 * Purely decorative: the hook's window listener owns the drop event, so this
 * never takes the pointer.
 */

const Backdrop = styled.div<{ $visible: boolean; $zIndex: number }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(3px);
  /* The window-level listener handles the drop, so the overlay must never
     intercept the pointer or the drag would end on this element instead. */
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 140ms ease, visibility 140ms ease;
`;

const Frame = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: min(560px, 100%);
  padding: 44px 32px;
  border: 2px dashed ${uiTokens.colors.accent};
  border-radius: ${uiTokens.radius.lg};
  background: ${uiTokens.colors.bgGlass};
  box-shadow: ${uiTokens.shadow.md};
  text-align: center;
  transform: ${({ $visible }) => ($visible ? 'scale(1)' : 'scale(0.98)')};
  transition: transform 140ms ease;
`;

const IconRing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${uiTokens.radius.pill};
  background: rgba(52, 211, 153, 0.12);
  color: ${uiTokens.colors.accent};
`;

const Title = styled.div`
  font-family: ${uiTokens.typography.sans};
  font-size: 1.05rem;
  font-weight: 600;
  color: ${uiTokens.colors.textPrimary};
`;

const Subtitle = styled.div`
  font-family: ${uiTokens.typography.sans};
  font-size: 0.85rem;
  color: ${uiTokens.colors.textSecondary};
`;

export interface FileDropOverlayProps {
  visible: boolean;
  title?: string;
  subtitle?: string;
  /**
   * Raise this above the host's own stacking context when the drop target sits
   * inside one — FacilityChatBubble's panel reaches 1300, for instance.
   */
  zIndex?: number;
  className?: string;
}

export const FileDropOverlay: React.FC<FileDropOverlayProps> = ({
  visible,
  title = 'Drop files to attach',
  subtitle = 'Images, PDFs, Word, Excel, CSV and text files',
  zIndex = 300,
  className,
}) => (
  <Backdrop $visible={visible} $zIndex={zIndex} className={className} aria-hidden={!visible}>
    <Frame $visible={visible}>
      <IconRing>
        <UploadCloud size={26} />
      </IconRing>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
    </Frame>
  </Backdrop>
);

export default FileDropOverlay;
